import { CheckCircle, XCircle, Loader2, AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { DataImport } from "@/hooks/use-data-imports"

interface StepProgressProps {
  importData: DataImport | null
  onClose: () => void
}

export function StepProgress({ importData, onClose }: StepProgressProps) {
  if (!importData) return null

  const { status, total_rows, imported_rows, failed_rows, errors_log } = importData

  const processed = imported_rows + failed_rows
  const progressPercent = total_rows > 0 ? Math.round((processed / total_rows) * 100) : 0

  const isProcessing = status === "processing"
  const isCompleted = status === "completed"
  const isFailed = status === "failed"

  return (
    <div className="space-y-4">
      {/* Status icon */}
      <div className="flex flex-col items-center py-4">
        {isProcessing && (
          <>
            <Loader2 className="w-12 h-12 text-primary animate-spin" />
            <p className="mt-3 text-sm font-medium">Importowanie danych...</p>
          </>
        )}
        {isCompleted && failed_rows === 0 && (
          <>
            <CheckCircle className="w-12 h-12 text-green-500" />
            <p className="mt-3 text-sm font-medium text-green-700 dark:text-green-400">
              Import zakonczony pomyslnie
            </p>
          </>
        )}
        {isCompleted && failed_rows > 0 && (
          <>
            <AlertTriangle className="w-12 h-12 text-amber-500" />
            <p className="mt-3 text-sm font-medium text-amber-700 dark:text-amber-400">
              Import zakonczony z bledami
            </p>
          </>
        )}
        {isFailed && (
          <>
            <XCircle className="w-12 h-12 text-destructive" />
            <p className="mt-3 text-sm font-medium text-destructive">
              Import nie powiodl sie
            </p>
          </>
        )}
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">Postep</span>
          <span className="text-xs font-medium">{progressPercent}%</span>
        </div>
        <div className="h-2.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              isFailed
                ? "bg-destructive"
                : isCompleted && failed_rows > 0
                  ? "bg-amber-500"
                  : "bg-primary"
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border p-3 text-center">
          <p className="text-2xl font-bold text-foreground">{total_rows}</p>
          <p className="text-xs text-muted-foreground">Wszystkich</p>
        </div>
        <div className="rounded-lg border p-3 text-center">
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {imported_rows}
          </p>
          <p className="text-xs text-muted-foreground">Zaimportowanych</p>
        </div>
        <div className="rounded-lg border p-3 text-center">
          <p className={`text-2xl font-bold ${failed_rows > 0 ? "text-destructive" : "text-foreground"}`}>
            {failed_rows}
          </p>
          <p className="text-xs text-muted-foreground">Blednych</p>
        </div>
      </div>

      {/* Errors */}
      {errors_log && errors_log.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">
            Bledy ({errors_log.length > 10 ? `pierwsze 10 z ${errors_log.length}` : errors_log.length})
          </h4>
          <div className="rounded-lg border divide-y max-h-[200px] overflow-y-auto">
            {errors_log.slice(0, 10).map((err, i) => (
              <div key={i} className="px-3 py-2 text-sm">
                <span className="font-mono text-xs text-muted-foreground mr-2">
                  Wiersz {err.row}:
                </span>
                <span className="text-destructive">{err.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Close button */}
      {!isProcessing && (
        <div className="flex justify-end">
          <Button onClick={onClose}>Zamknij</Button>
        </div>
      )}
    </div>
  )
}
